import React from "react";
import styles from "../../styles/winterregister.module.css";
import type { Cell, Status } from "./index";

/* ---------- 아이콘 ---------- */
const S = ({ size = 22, stroke = "currentColor", width = 1.5, children }: { size?: number; stroke?: string; width?: number; children: React.ReactNode }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
export const IconBack = () => <S><path d="M15 5l-7 7 7 7" /></S>;
export const IconHeadset = () => <S width={1}><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" /><path d="M21 16v2a4 4 0 0 1-4 4h-5" /></S>;
export const IconCal = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3.75" y="5.75" width="16.5" height="14.5" stroke="currentColor" strokeWidth="1.5" /><path d="M3.75 9.75h16.5M8 3.75v4M16 3.75v4" stroke="currentColor" strokeWidth="1.5" /></svg>;
export const IconChevron = ({ dir }: { dir: "l" | "r" }) => <S size={18}>{dir === "l" ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}</S>;

/* ---------- 상태 배지 (등록 가능 / 마감 임박 / 모집 마감) ---------- */
export const TONE: { [k in Status]: { t: string } } = {
    closed: { t: "모집 마감" },
    soon: { t: "마감 임박" },
    open: { t: "등록 가능" },
};
export const StatusBadge = ({ status, inverse }: { status: Status; inverse?: boolean }) => (
    <span className={styles.badge} data-tone={status} data-inverse={inverse ? "1" : "0"}>{TONE[status].t}</span>
);

/* ---------- 상단바: ← / 헤드셋 / 진행 바 3칸 ---------- */
export const TopBar = ({ wide, step, onBack, onQuick }: { wide: boolean; step: number; onBack: () => void; onQuick: () => void }) => (
    <header className={styles.hdr} data-wide={wide ? "1" : "0"}>
        <div className={styles.hdrIn}>
            <button type="button" className={styles.hdrBtn} aria-label="뒤로" onClick={onBack}><IconBack /></button>
            <button type="button" className={styles.hdrBtn} aria-label="빠른 상담" onClick={onQuick}><IconHeadset /></button>
            <div className={styles.progress}>
                <i data-on="1" /><i data-on={step >= 2 ? "1" : "0"} /><i data-on={step >= 3 ? "1" : "0"} />
            </div>
        </div>
    </header>
);

/* ---------- 프리윈터 / 정규윈터 세그 ---------- */
export const ProgSeg = ({ value, onChange }: { value: "pre" | "regular"; onChange: (v: "pre" | "regular") => void }) => (
    <div className={styles.seg}>
        {([["pre", "프리윈터"], ["regular", "정규윈터"]] as const).map(([k, label]) => (
            <button type="button" key={k} className={styles.segBtn} data-on={value === k ? "1" : "0"} onClick={() => onChange(k)}>{label}</button>
        ))}
    </div>
);

export const SegPair = ({ items, value, onChange }: { items: [string, string][]; value: string; onChange: (v: string) => void }) => (
    <div className={styles.seg}>
        {items.map(([k, label]) => (
            <button type="button" key={k} className={styles.segBtn} data-on={value === k ? "1" : "0"} onClick={() => onChange(k)}>{label}</button>
        ))}
    </div>
);

/* ---------- 지점 11개 (이름 + 배지) ---------- */
export const BranchGrid = ({ branches, picked, cellOf, onPick }: { branches: { ko: string; en: string }[]; picked: string; cellOf: (ko: string) => Cell | null; onPick: (ko: string) => void }) => (
    <div className={styles.branches}>
        {branches.map(b => {
            const c = cellOf(b.ko);
            const status: Status = c ? c.status : "open";
            const on = picked === b.ko;
            return (
                <button type="button" key={b.ko} className={styles.branch} data-on={on ? "1" : "0"} onClick={() => onPick(b.ko)}>
                    <span className={styles.branchName}>{b.ko}</span>
                    <StatusBadge status={status} inverse={on} />
                </button>
            );
        })}
    </div>
);

/* ---------- 입실 희망일 달력 ---------- */
export const MiniCal = ({ title, weekdays, cells, prevOff, nextOff, onPrev, onNext, onPick }: {
    title: string; weekdays: string[]; cells: { day: number | null; ok: boolean; on: boolean; today: boolean; dt?: Date }[];
    prevOff: boolean; nextOff: boolean; onPrev: () => void; onNext: () => void; onPick: (dt: Date) => void;
}) => (
    <div className={styles.cal}>
        <div className={styles.calHead}>
            <button type="button" className={styles.calNav} disabled={prevOff} onClick={onPrev} aria-label="이전 달"><IconChevron dir="l" /></button>
            <span className={styles.calTitle}>{title}</span>
            <button type="button" className={styles.calNav} disabled={nextOff} onClick={onNext} aria-label="다음 달"><IconChevron dir="r" /></button>
        </div>
        <div className={styles.calWeek}>{weekdays.map(w => <span key={w}>{w}</span>)}</div>
        <div className={styles.calGrid}>
            {cells.map((c, i) => (
                <div key={i} className={styles.calCellWrap}>
                    {c.day != null && (
                        <button type="button" className={styles.calCell} data-on={c.on ? "1" : "0"} data-today={c.today ? "1" : "0"} disabled={!c.ok} onClick={() => c.dt && onPick(c.dt)}>{c.day}</button>
                    )}
                </div>
            ))}
        </div>
    </div>
);

/* ---------- 등록 가능/마감 임박 팝업 → 등록하러 가기 ---------- */
export const PopUp = ({ badge, place, head, loading, err, onGo, onClose }: { badge: React.ReactNode; place: string; head: string; loading: boolean; err: string; onGo: () => void; onClose: () => void }) => (
    <div className={styles.popWrap} onClick={onClose} role="dialog" aria-modal="true">
        <div className={styles.pop} onClick={(e) => e.stopPropagation()}>
            <div className={styles.popBadge}>{badge}</div>
            <h4 className={styles.popTitle}>{place}<br /><b>{head}</b></h4>
            <p className={styles.popP}>결제 페이지에서 입실일을 고르고 바로 등록할 수 있어요.</p>
            {err && <p className={styles.errLine}>{err}</p>}
            <button type="button" className={styles.popGo} disabled={loading} onClick={onGo}>{loading ? "결제 페이지 여는 중…" : "등록하러 가기"}</button>
            <button type="button" className={styles.popClose} disabled={loading} onClick={onClose}>다른 지점 보기</button>
        </div>
    </div>
);

/* ---------- 완료 ---------- */
export const DoneView = ({ title, sub, hint, onConfirm }: { title: string; sub: string; hint: string; onConfirm: () => void }) => (
    <div className={styles.done}>
        <div className={styles.doneIcon}>✓</div>
        <h4 className={styles.doneTitle}>{title}</h4>
        <p className={styles.doneSub}>{sub}</p>
        {hint && <p className={styles.doneHint}>{hint}</p>}
        <button type="button" className={styles.doneBtn} onClick={onConfirm}>확인</button>
    </div>
);
